import { useNavigate, useParams } from 'react-router-dom';
import { useCategory, useUpdateCategory, useTranslation } from '@/hooks';
import { CreateCategoryRequest } from '@/types/model';
import CategoryForm from '@/components/forms/CategoryForm';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const EditCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { data: category, isLoading, isError, error } = useCategory(id!);
  const updateCategoryMutation = useUpdateCategory();

  const handleSuccess = async (data: CreateCategoryRequest) => {
    if (!id) return;

    try {
      await updateCategoryMutation.mutateAsync({ id, data });
      toast.success(t.categories.messages.updated);
      navigate(-1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-gray-400 mr-2" />
              <span className="text-gray-500">Cargando categoría...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !category) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar la categoría</h3>
              <p className="text-gray-500 mb-4">{error?.message || 'La categoría no fue encontrada'}</p>
              <button
                onClick={() => navigate('/content/categories')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Volver a Categorías
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <CategoryForm
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          initialData={category}
          isEdit={true}
          lockedType={category.type}
          isSubmitting={updateCategoryMutation.isPending}
        />
      </div>
    </div>
  );
};

export default EditCategory;
